import React from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import MeaPage from "../../components/MeaPage";
import { useAuthContext } from "../../hooks/useAuthContext";
import classesAPI from "../../services/classes";
import trainingsAPI from "../../services/trainings";
import scheduleAPI from "../../services/schedule";
import accountsAPI from "../../services/accounts";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { Skeleton } from "primereact/skeleton";

export function TeacherClassesDetailPage() {
  const { school } = useLoaderData();
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const { auth, currentMembership, updateCurrentMembershipPreferences } = useAuthContext();

  const trainingQuery = trainingsAPI.GetOne(school?.id, trainingId, auth?.access_token);
  const training = trainingQuery?.data?.data;

  const trainingsQuery = trainingsAPI.useGetList(
    school?.id,
    auth?.access_token,
    1,
    100,
    ""
  );
  const trainings = trainingsQuery?.data?.data || [];

  const classesQuery = classesAPI.useGetList(
    school?.id,
    trainingId,
    auth?.access_token,
    1,
    1000,
    {
      teacher_member_id: currentMembership?.id,
    }
  );

  const scheduleQuery = scheduleAPI.useGetList(
    school?.id,
    trainingId,
    auth?.access_token,
    1,
    1000,
    {}
  );

  const classes = classesQuery?.data?.data || [];
  const scheduleItems = scheduleQuery?.data?.data || [];

  const getStatusLabel = (status) => {
    switch (status) {
      case "OPEN":
        return "Ouvert";
      case "DRAFT":
        return "Brouillon";
      case "SUSPENDED":
        return "Suspendu";
      case "ARCHIVED":
        return "Archivé";
      default:
        return status;
    }
  };

  const itemTemplate = (option) => {
    return (
      <div className="flex align-items-center">
        <span className="font-medium text-color">{option.name}</span>
        <span
          className={`text-xs px-2 py-1 border-round font-semibold ml-2 ${
            option.status === "OPEN"
              ? "bg-green-100 text-green-800"
              : option.status === "DRAFT"
              ? "bg-blue-100 text-blue-800"
              : option.status === "SUSPENDED"
              ? "bg-red-100 text-red-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {getStatusLabel(option.status)}
        </span>
      </div>
    );
  };

  const valueTemplate = (option, props) => {
    if (!option) {
      const currentReg = trainings.find((t) => t.id === trainingId) || training;
      if (!currentReg) {
        return <span>{props.placeholder}</span>;
      }
      option = currentReg;
    }
    return (
      <div className="flex align-items-center">
        <span className="font-bold text-color mr-2">{option.name}</span>
        <span
          className={`text-xs px-2 py-0.5 border-round font-semibold ${
            option.status === "OPEN"
              ? "bg-green-100 text-green-800"
              : option.status === "DRAFT"
              ? "bg-blue-100 text-blue-800"
              : option.status === "SUSPENDED"
              ? "bg-red-100 text-red-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {getStatusLabel(option.status)}
        </span>
      </div>
    );
  };

  const handleTrainingChange = async (e) => {
    const newTrainingId = e.value;
    if (newTrainingId && newTrainingId !== trainingId) {
      try {
        const updatedPrefs = await accountsAPI.UpdateMemberPreferences(
          auth?.access_token,
          currentMembership?.id,
          newTrainingId
        );
        if (updatedPrefs) {
          updateCurrentMembershipPreferences(updatedPrefs);
        } else {
          const fallbackPrefs = currentMembership?.preferences
            ? { ...currentMembership.preferences, last_visited_training: newTrainingId }
            : { last_visited_training: newTrainingId };
          updateCurrentMembershipPreferences(fallbackPrefs);
        }
      } catch (err) {
        console.error("Failed to update last visited preference", err);
      }
      navigate(`/school/${school?.id}/teacher/classes/${newTrainingId}`);
    }
  };

  const sectionOptions = [
    { label: "Mes séances de cours", value: "sessions" },
    { label: "Mes classes", value: "classes" },
    { label: "Avis de passage", value: "avis-passage" },
    { label: "Agenda", value: "agenda" },
  ];

  const handleSectionChange = (e) => {
    const newSection = e.value;
    if (!newSection || newSection === "classes") {
      return;
    }

    if (newSection === "sessions") {
      navigate(`/school/${school?.id}/teacher/sessions/${trainingId}`);
      return;
    }

    if (newSection === "agenda") {
      navigate(`/school/${school?.id}/teacher/agenda/${trainingId}`);
      return;
    }

    navigate(`/school/${school?.id}/teacher/avis-passage/${trainingId}`);
  };

  const customWorkspaceBar = (
    <div className="w-full p-breadcrumb border-bottom-1 surface-border py-2 px-3 flex align-items-center flex-wrap gap-2">
      <div className="flex align-items-center gap-2">
        <i className="pi pi-th-large text-teal-600 text-sm"></i>
        <Dropdown
          value="classes"
          options={sectionOptions}
          onChange={handleSectionChange}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: "auto" }}
        />
      </div>
      <span className="text-300 font-light text-base sm:text-lg">/</span>
      <div className="flex align-items-center gap-2">
        <i className="pi pi-bookmark text-indigo-500 text-sm"></i>
        <Dropdown
          value={trainingId}
          options={trainings}
          optionLabel="name"
          optionValue="id"
          onChange={handleTrainingChange}
          placeholder={training?.name || "Sélectionner une formation"}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: "auto" }}
          itemTemplate={itemTemplate}
          valueTemplate={valueTemplate}
        />
      </div>
    </div>
  );

  const getClassScheduleDetails = (classeId) => {
    const classSchedules = scheduleItems.filter(
      (item) => item.class_id === classeId || item.classe?.id === classeId
    );

    const slotsList = [];
    const seenSlots = new Set();
    classSchedules.forEach((item) => {
      if (item.slot && !seenSlots.has(item.slot.id)) {
        seenSlots.add(item.slot.id);
        const timeStr =
          item.slot.start_hour && item.slot.end_hour
            ? ` (${item.slot.start_hour} - ${item.slot.end_hour})`
            : "";
        slotsList.push(`${item.slot.name}${timeStr}`);
      }
    });

    const classroomsList = [];
    const seenClassrooms = new Set();
    classSchedules.forEach((item) => {
      if (item.classroom && !seenClassrooms.has(item.classroom.id)) {
        seenClassrooms.add(item.classroom.id);
        const locStr = item.classroom.location ? ` (${item.classroom.location})` : "";
        classroomsList.push(`${item.classroom.name}${locStr}`);
      }
    });

    return {
      slotName: slotsList.length > 0 ? slotsList.join(", ") : "-",
      classroomName: classroomsList.length > 0 ? classroomsList.join(", ") : "-",
    };
  };

  const renderClassCard = (classe) => {
    const { slotName, classroomName } = getClassScheduleDetails(classe.id);

    return (
      <div key={classe.id} className="col-12 md:col-6 xl:col-4 p-3">
        <Card
          className="h-full cursor-pointer hover:shadow-4 hover:border-teal-500 transition-all transition-duration-200"
          style={{
            borderLeft: "5px solid var(--teal-500)",
            borderRadius: "8px",
          }}
          onClick={() =>
            navigate(
              `/school/${school?.id}/teacher/classes/${trainingId}/classe/${classe.id}`
            )
          }
        >
          <div className="flex flex-column h-full">
            <div className="flex align-items-center mb-3">
              <div
                className="mr-3 flex align-items-center justify-content-center bg-teal-50 text-teal-600 border-round flex-shrink-0"
                style={{ width: "48px", height: "48px" }}
              >
                <img
                  src="/images/icons/trainings.png"
                  style={{ width: "36px", height: "36px" }}
                  alt="classe"
                />
              </div>
              <span className="text-900 font-bold text-xl m-0 line-height-2 flex-1">
                {classe.name}
              </span>
            </div>

            <div className="flex-1">
              {classe.presentation && (
                <p className="text-600 text-sm mb-3 line-height-3">
                  {classe.presentation.length > 100
                    ? classe.presentation.substring(0, 100) + "..."
                    : classe.presentation}
                </p>
              )}

              <div className="surface-50 p-3 border-round mb-3 flex flex-column gap-2 text-sm border-1 border-100">
                <div className="flex align-items-center text-700">
                  <i className="pi pi-clock mr-2 text-teal-600 font-semibold"></i>
                  <span className="font-semibold text-800 mr-1">Créneau :</span>
                  <span className="text-700">{slotName}</span>
                </div>
                <div className="flex align-items-center text-700">
                  <i className="pi pi-building mr-2 text-teal-600 font-semibold"></i>
                  <span className="font-semibold text-800 mr-1">Salle :</span>
                  <span className="text-700">{classroomName}</span>
                </div>
              </div>

              <div className="flex justify-content-between align-items-center text-sm mt-auto pt-2 border-top-1 border-200">
                <div className="flex align-items-center flex-wrap gap-2">
                  <span className="text-600 text-sm flex align-items-center gap-1">
                    <i className="pi pi-users text-teal-500"></i>
                    {classe.total_students || 0} {classe.total_students > 1 ? "élèves" : "élève"}
                  </span>
                  {classe.referent && (
                    <>
                      <span className="text-300">|</span>
                      <span className="text-600 text-sm flex align-items-center gap-1">
                        <i className="pi pi-user text-teal-500"></i>
                        {classe.referent.name
                          ? classe.referent.name
                          : `${classe.referent.lastname?.toUpperCase() || ""} ${classe.referent.firstname || ""}`.trim()}
                      </span>
                    </>
                  )}
                </div>
                <span className="text-teal-600 font-semibold text-xs flex align-items-center gap-1 hover:text-teal-800">
                  Accéder <i className="pi pi-arrow-right text-xs"></i>
                </span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  };

  const renderSkeleton = () => (
    <div className="col-12 md:col-6 xl:col-4 p-3">
      <Card>
        <div className="flex align-items-center mb-3">
          <Skeleton width="48px" height="48px" className="mr-3 border-round" />
          <div className="flex flex-column flex-1">
            <Skeleton width="70%" height="1.4rem" className="mb-2" />
            <Skeleton width="40%" height="1rem" />
          </div>
        </div>
        <Skeleton width="100%" height="3rem" className="mb-3" />
        <Skeleton width="100%" height="4rem" className="mb-3" />
        <div className="flex justify-content-between">
          <Skeleton width="30%" height="1rem" />
          <Skeleton width="30%" height="1rem" />
        </div>
      </Card>
    </div>
  );

  return (
    <MeaPage
      showBackButton={true}
      onBackButtonClick={() => navigate(`/school/${school?.id}/teacher`)}
      title="Mes classes"
      breadcrumb={customWorkspaceBar}
    >
      <div className="grid grid-nogutter pt-3 px-4 md:px-6 lg:px-8">
        {classesQuery.isLoading && (
          <div className="grid w-full">
            {[1, 2, 3].map((i) => (
              <React.Fragment key={i}>{renderSkeleton()}</React.Fragment>
            ))}
          </div>
        )}

        {classesQuery.isSuccess && classes.length === 0 && (
          <div className="col-12 p-3 text-center my-5 w-full">
            <i className="pi pi-folder-open text-400 text-5rem mb-3"></i>
            <div className="text-900 text-xl font-bold mb-1">Aucune classe assignée</div>
            <div className="text-600 text-sm">
              Vous n'êtes assigné(e) à aucune classe dans la formation sélectionnée.
            </div>
          </div>
        )}

        {classesQuery.isSuccess && classes.length > 0 && (
          <div className="grid w-full">
            {classes.map(renderClassCard)}
          </div>
        )}
      </div>
    </MeaPage>
  );
}

export default TeacherClassesDetailPage;
